/**
 * Note Tabs Component
 *
 * Navigation tabs for switching between note views:
 * - Note: Main note editing view
 * - Transcript: Audio transcript view (if available)
 * - Other Notes: Additional notes from the same encounter (if available)
 * - JotDocs: AI-generated supplementary documents (if available)
 *
 * This is a controlled component - parent manages active tab and navigation.
 */

import React, { useEffect,useRef } from 'react';
import { Assessment as AnalysisIcon, Subject as SubjectIcon } from '@mui/icons-material';
import { Box, CircularProgress, Container, Tab, Tabs, Tooltip } from '@mui/material';

import { LAYOUT_SPACING } from '../constants/layout';

export interface NoteTabsProps {
  /**
   * Current active tab value
   * - 'note': Main note view (legacy, when only one note)
   * - 'transcript': Transcript view
   * - 'encounter-analysis': Encounter analysis view
   * - 'note-{id}': Note document view (used when multiple notes exist)
   * - 'jotdoc-{id}': JotDoc document view
   */
  value: string;

  /**
   * Tab change handler
   * Parent component handles navigation logic
   */
  onChange: (event: React.SyntheticEvent, newValue: string) => void;

  /**
   * Whether transcript tab should be shown
   * @default false
   */
  hasTranscript?: boolean;

  /**
   * Whether encounter analysis tab should be shown
   * @default false
   */
  hasEncounterAnalysis?: boolean;

  /**
   * Title of the current note being viewed
   * Used for the "Note" tab label when only one note exists
   * Falls back to "Note" if not provided
   * @deprecated Use allNotes instead for multiple note support
   */
  currentNoteTitle?: string | null;

  /**
   * All notes from the encounter to display as tabs
   * When provided, displays all notes in stable order
   * Each note becomes a navigable tab with value 'note-{id}'
   * @default []
   */
  allNotes?: Array<{
    encounter_document_id: string;
    title?: string;
    created_at?: string;
    isPending?: boolean;
  }>;

  /**
   * Other notes from the same encounter (excluding current)
   * @deprecated Use allNotes instead for stable tab order
   * @default []
   */
  otherNotes?: Array<{
    encounter_document_id: string;
    title?: string;
    created_at?: string;
    isPending?: boolean;
  }>;

  /**
   * Jotdoc items to display as tabs
   * Each jotdoc becomes a navigable tab
   * @default []
   */
  jotdocs?: Array<{
    jotdoc_id: string;
    title?: string;
  }>;
}

/**
 * NoteTabs - Navigation tabs for note view modes
 *
 * Presentation component that renders MUI tabs for switching between
 * different views of a note (note content, transcript, other notes, jotdocs).
 *
 * Responsibilities:
 * - Render tab bar with appropriate tabs
 * - Apply consistent styling and spacing
 * - Handle tab selection UI state
 *
 * Parent responsibilities:
 * - Determine which tabs to show (hasTranscript, otherNotes, jotdocs)
 * - Handle navigation when tabs are clicked (onChange)
 * - Track current view mode (value)
 *
 * @example
 * ```tsx
 * <NoteTabs
 *   value={isTranscriptMode ? 'transcript' : 'note'}
 *   onChange={handleTabChange}
 *   hasTranscript={true}
 *   otherNotes={[
 *     { encounter_document_id: '123', title: 'Follow-up Note' }
 *   ]}
 *   jotdocs={[
 *     { jotdoc_id: '456', title: 'Treatment Plan' }
 *   ]}
 * />
 * ```
 */
export const NoteTabs: React.FC<NoteTabsProps> = ({
  value,
  onChange,
  hasTranscript = false,
  hasEncounterAnalysis = false,
  currentNoteTitle,
  allNotes,
  otherNotes = [],
  jotdocs = [],
}) => {
  // Use allNotes if provided, otherwise fall back to otherNotes for backwards compatibility
  const notesToDisplay = allNotes || otherNotes;
  const hasMultipleNotes = notesToDisplay.length > 1 || (notesToDisplay.length === 1 && allNotes);

  // Determine the label for the current note tab (only used in legacy single-note mode)
  const noteTabLabel = currentNoteTitle && currentNoteTitle.trim() ? currentNoteTitle : 'Note';

  // Ref to track scroll position and prevent auto-scrolling
  const tabsRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);

  // Prevent auto-scroll when tab changes - maintain scroll position
  useEffect(() => {
    const tabsElement = tabsRef.current?.querySelector('.MuiTabs-scrollableContainer') as HTMLElement;
    if (!tabsElement) return;

    // Save current scroll position
    const savedScrollLeft = tabsElement.scrollLeft;
    scrollPositionRef.current = savedScrollLeft;

    // Restore scroll position after MUI's auto-scroll attempts
    // Use multiple RAF calls to catch MUI's scroll timing
    let rafId2: number | null = null;
    const rafId1 = requestAnimationFrame(() => {
      rafId2 = requestAnimationFrame(() => {
        // Restore the saved scroll position if it changed
        if (tabsElement.scrollLeft !== savedScrollLeft) {
          tabsElement.scrollLeft = savedScrollLeft;
        }
      });
    });

    return () => {
      cancelAnimationFrame(rafId1);
      if (rafId2 !== null) {
        cancelAnimationFrame(rafId2);
      }
    };
  }, [value]);

  // Sort notes by created_at to maintain document order
  const sortedNotes = [...notesToDisplay].sort((a, b) => {
    if (!a.created_at && !b.created_at) return 0;
    if (!a.created_at) return 1;
    if (!b.created_at) return -1;
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });

  return (
    <Container
      maxWidth="xxl"
      disableGutters
      sx={theme => ({
        bgcolor: 'background.paper',
        px: LAYOUT_SPACING.SECTION_HORIZONTAL_PADDING,
        width: '100%',
        // No sticky here - parent container handles sticky behavior
        borderBottom: `1px solid ${theme.palette.divider}`,
      })}
    >
      <Tabs
        ref={tabsRef}
        value={value}
        onChange={onChange}
        textColor="primary"
        indicatorColor="primary"
        aria-label="note view tabs"
        variant="scrollable"
        scrollButtons="auto"
        sx={theme => ({
          '& .MuiTab-root': {
            textTransform: 'none',
            minHeight: 48,
            fontSize: '0.9375rem',
            fontWeight: 500,
            maxWidth: 200,
            minWidth: 0, // Allow tabs to shrink below content size
          },
          '& .MuiTab-root .MuiTab-wrapper': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
          },
        })}
      >
        {/* Transcript tab first (all the way to the left) */}
        {hasTranscript && (
          <Tab
            value="transcript"
            label="Transcript"
            icon={<SubjectIcon />}
            iconPosition="start"
          />
        )}

        {/* Encounter Analysis tab */}
        {/* TODO: Temporarily disabled - uncomment when ready to show */}
        {/* {hasEncounterAnalysis && (
          <Tab
            value="encounter-analysis"
            label="Analysis"
            icon={<AnalysisIcon />}
            iconPosition="start"
          />
        )} */}
        
        {/* Show all notes when multiple exist, or single legacy note tab */}
        {hasMultipleNotes ? (
          // Multiple notes mode: show all notes with note-{id} values
          sortedNotes.map(note => {
            const label = note.title && note.title.trim() ? note.title : 'Note';
            return (
              <Tab
                key={note.encounter_document_id}
                value={`note-${note.encounter_document_id}`}
                label={
                  <Tooltip title={label} arrow>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        opacity: note.isPending ? 0.7 : 1,
                        minWidth: 0,
                        maxWidth: '100%',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '100%',
                          flex: 1,
                        }}
                      >
                        {label}
                      </Box>
                      {note.isPending && <CircularProgress size={12} sx={{ color: 'inherit', flexShrink: 0 }} />}
                    </Box>
                  </Tooltip>
                }
              />
            );
          })
        ) : (
          // Legacy single note mode: show simple "Note" tab
          <Tab
            value="note"
            label={
              <Tooltip title={noteTabLabel} arrow>
                <Box
                  component="span"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                    display: 'block',
                  }}
                >
                  {noteTabLabel}
                </Box>
              </Tooltip>
            }
          />
        )}
        
        {/* Legacy: Other notes in document order (only shown when allNotes not provided) */}
        {!allNotes && sortedNotes.map(note => {
          const label = note.title && note.title.trim() ? note.title : 'Note';
          return (
            <Tab
              key={note.encounter_document_id}
              value={`note-${note.encounter_document_id}`}
              label={
                <Tooltip title={label} arrow>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      opacity: note.isPending ? 0.7 : 1,
                      minWidth: 0, // Allow flex item to shrink
                      maxWidth: '100%',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                        flex: 1,
                      }}
                    >
                      {label}
                    </Box>
                    {note.isPending && <CircularProgress size={12} sx={{ color: 'inherit', flexShrink: 0 }} />}
                  </Box>
                </Tooltip>
              }
            />
          );
        })}
        {jotdocs.map(jotdoc => {
          const jotdocLabel = jotdoc.title || 'Document';
          return (
            <Tab
              key={jotdoc.jotdoc_id}
              value={`jotdoc-${jotdoc.jotdoc_id}`}
              label={
                <Tooltip title={jotdocLabel} arrow>
                  <Box
                    component="span"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                      display: 'block',
                    }}
                  >
                    {jotdocLabel}
                  </Box>
                </Tooltip>
              }
            />
          );
        })}
      </Tabs>
    </Container>
  );
};
